/**
 * AppLayout — root layout (Sidebar + Navbar + Outlet)
 *
 * ProtectedRoute ichida render bo'ladi — faqat authenticated user'lar ko'radi.
 * Mobile sidebar toggle + ErrorBoundary + WebSocketProvider.
 */

import { Loader2 } from 'lucide-react';
import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router';

import { ErrorBoundary } from '@/components/error-boundary';
import { useGlobalScanner } from '@/features/kassa/use-global-scanner';
import { WebSocketProvider } from '@/providers/websocket-provider';
import { cn } from '@/shared/lib/cn';

import { Navbar } from './Navbar';
import { Sidebar } from './Sidebar';
import { useModalScrollLock } from './use-modal-scroll-lock';

export function AppLayout(): React.ReactElement {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  // Global skaner: qayerda bo'lsa ham barcode skanerlansa → kassaga qo'shadi
  useGlobalScanner();
  // Modal ochiq bo'lganda orqa sahifa (<main>) scroll'ini qulflaydi
  useModalScrollLock(mainRef);

  // Mobil drawer ochiq bo'lsa: Escape bilan yopish (klaviatura/accessibility).
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [sidebarOpen]);

  return (
    <WebSocketProvider>
      <div className="flex h-dvh overflow-hidden text-foreground">
        <Sidebar
          open={sidebarOpen}
          onClose={() => {
            setSidebarOpen(false);
          }}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <Navbar
            onMobileMenuToggle={() => {
              setSidebarOpen((prev) => !prev);
            }}
          />
          <main
            ref={mainRef}
            className={cn(
              'min-h-0 flex-1',
              // Mobil drawer ochiqligida orqa fon scroll qilinmasin (iOS bleed).
              sidebarOpen ? 'overflow-hidden lg:overflow-y-auto' : 'overflow-y-auto',
            )}
          >
            <ErrorBoundary>
              {/* flex h-full flex-col: wrapper ANIQ <main> balandligida —
                  shunda fixed-viewport sahifa (Qarzlar karta) flex-1 bilan
                  cheklanadi (ichki scroll). Baland oddiy sahifalar kontenti
                  overflow bo'lib <main> scroll qiladi (min-height:auto), shuning
                  uchun ular buzilmaydi. */}
              <div className="flex h-full w-full flex-col p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6 sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]">
                {/* Lazy route chunk yuklanayotganda faqat kontent maydonida
                    spinner — sidebar/navbar darhol ko'rinadi (tez his). */}
                <Suspense
                  fallback={
                    <div className="flex min-h-[50vh] items-center justify-center">
                      <Loader2
                        className="size-6 animate-spin text-muted-foreground"
                        aria-label="Yuklanmoqda"
                      />
                    </div>
                  }
                >
                  <Outlet />
                </Suspense>
              </div>
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </WebSocketProvider>
  );
}
